import { useMutation } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { message } from "antd";

import createAffiliatePortalInstance from "../../api/AffiliatePortalInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AffiliateContractDownloadData,
  AffiliateDownloadData,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import { AppStrings } from "../../utils/constants/AppStrings";
import { triggerBrowserDownload } from "../../utils/triggerBrowserDownload";
import { getPrivateInstance } from "../_privateInstance";

// Surfaces the BE envelope message when present, otherwise a generic toast.
const notifyDownloadError = (error: unknown): void => {
  const envelopeMessage = (
    error as { response?: { data?: { message?: string } } }
  )?.response?.data?.message;
  message.error(envelopeMessage || AppStrings.AFFILIATE_REPORT_DOWNLOAD_FAILED);
};

// Filters mirror `GET /v1/affiliates` so the export matches the table.
export interface AffiliateListDownloadParams {
  status?: string;
  plan_type?: string;
  search?: string;
  start_date?: string;
  end_date?: string;
}

export interface AffiliateDateRangeParams {
  start_date?: string;
  end_date?: string;
}

// BE enforces both-or-neither on the date range, so the
// pair is only sent when complete.
const buildRangeParams = (
  params: AffiliateDateRangeParams,
): Record<string, string> =>
  params.start_date && params.end_date
    ? { start_date: params.start_date, end_date: params.end_date }
    : {};

const buildListParams = (
  params: AffiliateListDownloadParams,
): Record<string, string> => {
  const query: Record<string, string> = { ...buildRangeParams(params) };
  if (params.status) query.status = params.status;
  if (params.plan_type) query.plan_type = params.plan_type;
  if (params.search?.trim()) query.q = params.search.trim();
  return query;
};

// Operator list export. Drains nothing client-side — BE builds the CSV and
// returns a signed URL we hand to the browser.
export const useAffiliateListReportDownload = (): UseMutationResult<
  AffiliateDownloadData,
  Error,
  AffiliateListDownloadParams
> =>
  useMutation<AffiliateDownloadData, Error, AffiliateListDownloadParams>({
    mutationFn: async (params) => {
      const response = await getPrivateInstance().get<
        ApiSuccessEnvelope<AffiliateDownloadData>
      >(API_ROUTES.AFFILIATE_LIST_DOWNLOAD, { params: buildListParams(params) });
      return response.data.data;
    },
    onSuccess: (data) => triggerBrowserDownload(data.download_url),
    onError: notifyDownloadError,
  });

// Per-affiliate export (two-section CSV: KPI summary + per-code metrics).
export const useAffiliateDetailReportDownload = (): UseMutationResult<
  AffiliateDownloadData,
  Error,
  { affiliateId: string } & AffiliateDateRangeParams
> =>
  useMutation<
    AffiliateDownloadData,
    Error,
    { affiliateId: string } & AffiliateDateRangeParams
  >({
    mutationFn: async ({ affiliateId, ...range }) => {
      const path = API_ROUTES.AFFILIATE_DETAIL_DOWNLOAD.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      );
      const response = await getPrivateInstance().get<
        ApiSuccessEnvelope<AffiliateDownloadData>
      >(path, { params: buildRangeParams(range) });
      return response.data.data;
    },
    onSuccess: (data) => triggerBrowserDownload(data.download_url),
    onError: notifyDownloadError,
  });

// Affiliate-facing portal export — uses the portal axios instance so the
// affiliate's AccessToken is attached.
export const usePortalViewReportDownload = (): UseMutationResult<
  AffiliateDownloadData,
  Error,
  AffiliateDateRangeParams
> =>
  useMutation<AffiliateDownloadData, Error, AffiliateDateRangeParams>({
    mutationFn: async (range) => {
      const instance = createAffiliatePortalInstance();
      const response = await instance.get<
        ApiSuccessEnvelope<AffiliateDownloadData>
      >(API_ROUTES.AFFILIATE_PORTAL_VIEW_DOWNLOAD, {
        params: buildRangeParams(range),
      });
      return response.data.data;
    },
    onSuccess: (data) => triggerBrowserDownload(data.download_url),
    onError: notifyDownloadError,
  });

// Signed-GET download of an affiliate's uploaded contract PDF. Distinct
// from the CSV reports above (no date range, no row_count) but shares the
// same signed-URL → triggerBrowserDownload flow.
export const useAffiliateContractDownload = (): UseMutationResult<
  AffiliateContractDownloadData,
  Error,
  { affiliateId: string }
> =>
  useMutation<AffiliateContractDownloadData, Error, { affiliateId: string }>({
    mutationFn: async ({ affiliateId }) => {
      const path = API_ROUTES.AFFILIATE_CONTRACT_DOWNLOAD_URL.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      );
      const response = await getPrivateInstance().get<
        ApiSuccessEnvelope<AffiliateContractDownloadData>
      >(path);
      return response.data.data;
    },
    onSuccess: (data) => triggerBrowserDownload(data.download_url),
    onError: notifyDownloadError,
  });
